import React from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowLeft, Mail, MapPin, Phone, ShieldCheck, Sparkles } from "lucide-react";
import "./LegalPage.css";

const company = {
    name: "Shriansh Nexus",
    owner: "Shriansh AI",
    email: "hello@shriansh.ai",
    phone: "+91 9979501305",
    address: "Ahmedabad, Gujarat, India"
};

const pageContent = {
    "/terms": {
        title: "Terms & Conditions",
        subtitle: "Rules and conditions for using Shriansh Nexus.",
        sections: [
            {
                title: "1. Acceptance of Terms",
                body: "By accessing or using Shriansh Nexus, you agree to these Terms & Conditions. If you do not agree, please do not use the platform."
            },
            {
                title: "2. Service Description",
                body: "Shriansh Nexus is an AI-powered website generation and CRM platform that helps users create, preview, publish, and manage business websites and related CRM data."
            },
            {
                title: "3. User Responsibilities",
                body: "Users are responsible for the accuracy of business details, uploaded content, published website content, customer data, and compliance with applicable laws."
            },
            {
                title: "4. AI Generated Content",
                body: "The platform may generate text, layouts, images, suggestions, and website sections using AI. Users should review and approve content before publishing."
            },
            {
                title: "5. Payments and Subscriptions",
                body: "Paid plans may be billed monthly, yearly, or as otherwise displayed on the pricing page. Payments are processed through supported payment partners such as Razorpay."
            },
            {
                title: "6. Account Suspension",
                body: "We may restrict or suspend access if the platform is misused, used for illegal activity, payment abuse, spam, harmful content, or violation of these terms."
            },
            {
                title: "7. Limitation of Liability",
                body: "Shriansh Nexus is provided on an as-is and as-available basis. We are not liable for indirect losses, business losses, or third-party service interruptions."
            },
            {
                title: "8. Contact",
                body: `For questions about these terms, contact us at ${company.email}.`
            }
        ]
    },
    "/privacy": {
        title: "Privacy Policy",
        subtitle: "How we collect, use, and protect user information.",
        sections: [
            {
                title: "1. Information We Collect",
                body: "We may collect name, email address, phone number, business details, website generation inputs, CRM data, leads, usage data, and payment-related metadata."
            },
            {
                title: "2. How We Use Information",
                body: "We use information to create accounts, generate websites, provide CRM features, process subscriptions, improve the platform, provide support, and prevent abuse."
            },
            {
                title: "3. Payment Information",
                body: "Payments are processed through payment partners such as Razorpay. We do not store complete card, UPI, net banking, or sensitive payment credentials on our servers."
            },
            {
                title: "4. Third-Party Services",
                body: "We may use trusted third-party services for hosting, payments, AI processing, analytics, email, and infrastructure operations."
            },
            {
                title: "5. Data Security",
                body: "We apply reasonable technical and organizational safeguards to protect user data. However, no internet-based service can guarantee absolute security."
            },
            {
                title: "6. User Rights",
                body: "Users may contact us to request access, correction, or deletion of their information, subject to legal, billing, and operational requirements."
            },
            {
                title: "7. Contact",
                body: `For privacy-related questions, contact us at ${company.email}.`
            }
        ]
    },
    "/refund-policy": {
        title: "Refund & Cancellation Policy",
        subtitle: "Subscription cancellation and refund rules for Shriansh Nexus.",
        sections: [
            {
                title: "1. Subscription Cancellation",
                body: "Users may cancel future subscription renewals by contacting support or using available account settings when enabled."
            },
            {
                title: "2. Refund Policy",
                body: "Subscription payments are generally non-refundable once the billing cycle has started, because Shriansh Nexus is a digital SaaS product with instant access to platform features."
            },
            {
                title: "3. Duplicate or Failed Payments",
                body: "If a duplicate payment, failed transaction debit, or technical billing issue occurs, users may contact support. Valid cases will be reviewed and refunded to the original payment method."
            },
            {
                title: "4. Refund Timeline",
                body: "Approved refunds may take 5 to 10 business days to reflect, depending on the bank, card network, UPI provider, or payment partner."
            },
            {
                title: "5. Plan Downgrade",
                body: "If a user downgrades a plan, the updated plan may apply from the next billing cycle unless otherwise communicated."
            },
            {
                title: "6. Contact",
                body: `For refund or cancellation requests, contact ${company.email}.`
            }
        ]
    },
    "/delivery-policy": {
        title: "Delivery / Shipping Policy",
        subtitle: "Digital delivery terms for Shriansh Nexus.",
        sections: [
            {
                title: "1. Digital Product",
                body: "Shriansh Nexus is a digital SaaS platform. No physical product is shipped."
            },
            {
                title: "2. Delivery Method",
                body: "After signup or successful payment, users receive digital access to the platform through their account dashboard."
            },
            {
                title: "3. Delivery Timeline",
                body: "Access is generally provided instantly after account creation or payment confirmation. In rare cases, activation may take additional time due to payment verification or technical checks."
            },
            {
                title: "4. Website Publishing",
                body: "Generated websites can be previewed and published digitally through the platform, subject to plan limits, system status, and hosting configuration."
            },
            {
                title: "5. Contact",
                body: `For access or delivery-related questions, contact ${company.email}.`
            }
        ]
    },
    "/contact": {
        title: "Contact Us",
        subtitle: "Get in touch with Shriansh Nexus.",
        sections: [
            {
                title: "Support",
                body: `For product support, billing questions, onboarding, or demo requests, contact us at ${company.email}.`
            },
            {
                title: "Business Address",
                body: company.address
            },
            {
                title: "Phone",
                body: company.phone
            },
            {
                title: "Email",
                body: company.email
            }
        ]
    }
};

export default function LegalPage() {
    const location = useLocation();
    const content = pageContent[location.pathname] || pageContent["/terms"];

    return (
        <div className="legal-page">
            <div className="legal-bg" />

            <main className="legal-container">
                <Link to="/" className="legal-back">
                    <ArrowLeft size={16} />
                    Back to home
                </Link>

                <section className="legal-hero">
                    <div className="legal-brand">
                        <div className="legal-brand-logo">
                            <Sparkles size={20} />
                        </div>
                        <div>
                            <div className="legal-brand-title">{company.name}</div>
                            <div className="legal-brand-subtitle">Built by {company.owner}</div>
                        </div>
                    </div>

                    <div className="legal-pill">
                        <ShieldCheck size={16} />
                        <span>Policy information</span>
                    </div>

                    <h1>{content.title}</h1>
                    <p>{content.subtitle}</p>
                </section>

                <section className="legal-card">
                    {content.sections.map((section) => (
                        <div className="legal-section" key={section.title}>
                            <h2>{section.title}</h2>
                            <p>{section.body}</p>
                        </div>
                    ))}
                </section>

                <section className="legal-contact-card">
                    <h2>Business Contact Details</h2>

                    <div className="legal-contact-grid">
                        <div>
                            <Mail size={18} />
                            <span>{company.email}</span>
                        </div>
                        <div>
                            <Phone size={18} />
                            <span>{company.phone}</span>
                        </div>
                        <div>
                            <MapPin size={18} />
                            <span>{company.address}</span>
                        </div>
                    </div>
                </section>
            </main>
        </div>
    );
}